/**
 * App_Floodgate.ts
 *
 * The entry point for floodgate.
 */

import * as App_Common from "./App_Common";
import * as UI from "./UI/UI";
import * as Configuration from "./Configuration/Configuration";
import * as Window from "./Window/Window";
import AdaptiveSurveyLauncherFactory from "./FloodgateCore/AdaptiveSurveyLauncherFactory";
import TransporterFactory from "./FloodgateCore/TransporterFactory";
import CustomUISurvey from "./FloodgateCore/UISurvey/CustomUISurvey";
import DummyUISurvey from "./FloodgateCore/UISurvey/DummyUISurvey";
import FloodgateEnvironmentProvider from "./FloodgateCore/FloodgateEnvironmentProvider";
import FloodgateStorageProvider from "./FloodgateCore/FloodgateStorageProvider";
import FloodgateStringProvider from "./FloodgateCore/FloodgateStringProvider";
import FloodgateTelemetryLogger from "./FloodgateCore/FloodgateTelemetryLogger";
import ICustomSurvey from "./FloodgateCore/ICustomSurvey";
import IUISurvey from "./FloodgateCore/UISurvey/IUISurvey";
import { TargetedMessagingCampaignDefinitionProvider } from
	"./TargetedMessaging/TargetedMessagingCampaignDefinitionProvider";

import { FloodgateEngine } from "@ms-ofb/officefloodgatecore";
import { FilterValidCampaignDefinitions } from
	"@ms-ofb/officefloodgatecore/dist/src/Campaign/CampaignDefinitionProvider";
import { IFloodgateSettingIdMap, IFloodgateStorageProvider } from "@ms-ofb/officefloodgatecore/dist/src/Api/Api";
import { TelemetryEvent } from "@ms-ofb/officefloodgatecore/dist/src/Constants";

import { Promise } from "es6-promise";

/**
 * Has floodgate been initialized
 */
let initialized: boolean = false;

/**
 * Has the control been opened. Prevents more than one dialog being generated.
 */
let opened: boolean = false;
let startCount: number = 0;

let engine: FloodgateEngine;
let engineStartPromise: Promise<any> = null;
let engineTmsProvider: TargetedMessagingCampaignDefinitionProvider = null;

export function setEngineStartPromise(startPromise: Promise<any>): any {
	engineStartPromise = startPromise;
}

export function getEngineStartPromise(): Promise<any> {
	return engineStartPromise;
}

/**
 * Initialize. Must be called before any other call to floodgate.
 * @returns A promise which will be rejected if the call fails.
 */
export function initialize(): Promise<any> {
	return new Promise((resolve, reject) => {
		const rejectAndLogError = (errorMessage: string) => {
			const telemetryLogger = FloodgateEngine.getTelemetryLogger();
			if (telemetryLogger) {
				telemetryLogger.log_Event(TelemetryEvent.Floodgate.Initialize.Failed, { ErrorMessage: errorMessage });
			}

			reject(errorMessage);
		};

		if (!FloodgateStorageProvider.isStorageAvailable()) {
			rejectAndLogError("window.localStorage is not available.");
			return;
		}

		if (!initialized) {
			App_Common.initialize()
			.then(
				function onInitializeFulfilled() {
					Window.get().OfficeBrowserFeedback.floodgate = Window.get().OfficeBrowserFeedback.floodgate || {} as any;

					let floodgateInitOptions = Window.get().OfficeBrowserFeedback.floodgate.initOptions;
					floodgateInitOptions = floodgateInitOptions || {} as any;

					// Validate and copy campaign definitions to local storage
					if (floodgateInitOptions.campaignDefinitions !== undefined) {
						const filteredResult = FilterValidCampaignDefinitions(floodgateInitOptions.campaignDefinitions);

						if (filteredResult.error) {
							rejectAndLogError("floodgate.initOptions.campaignDefinitions error: " + filteredResult.error);
							return;
						}

						(new FloodgateStorageProvider()).write(
							IFloodgateStorageProvider.FileType.CampaignDefinitions,
							JSON.stringify(floodgateInitOptions.campaignDefinitions)
						);
					}

					Configuration.get().setFloodgateInitOptions(Window.get().OfficeBrowserFeedback.floodgate.initOptions);

					FloodgateEngine.setTelemetryLogger(new FloodgateTelemetryLogger());

					initialized = true;
					resolve();
				}
			)
			.catch(
				function onInitializeRejected(err) {
					rejectAndLogError("Initialization failed: {" + err + "}");
				}
			);
		} else {
			resolve();
		}
	});
}

/**
 * To support the multi-window scenario on web we need to initialize the engine on each resume.
 * Hence this separate method which does make() and start() together.
 */
export function startInternal(loadSynchronously: boolean, customEngine: FloodgateEngine = null): Promise<any> {
	if (engineStartPromise) {
		return engineStartPromise;
	}

	startCount++;
	const startPromise = new Promise((resolve, reject) => {
		const rejectAndLogError = (errorMessage: string) => {
			const telemetryLogger = FloodgateEngine.getTelemetryLogger();
			if (telemetryLogger) {
				telemetryLogger.log_Event(TelemetryEvent.Floodgate.Start.Failed, { Count: startCount, ErrorMessage: errorMessage });
			}

			reject(errorMessage);
		};

		try {
			if (!customEngine && !engineTmsProvider) {
				// AssetsProvider wraps auth callbacks which should not run every time we start()
				engineTmsProvider = new TargetedMessagingCampaignDefinitionProvider();
			}

			engine = customEngine || FloodgateEngine.make(
				Configuration.get().getCommonInitOptions().build || "",
				AdaptiveSurveyLauncherFactory.make(),
				Configuration.get().getFloodgateInitOptions().onSurveyActivatedCallback,
				new FloodgateStorageProvider(),
				Configuration.get().getFloodgateInitOptions().settingStorageCallback,
				new FloodgateStringProvider(),
				new FloodgateEnvironmentProvider(),
				new TransporterFactory(),
				[engineTmsProvider],
			);

			if (!engine) {
				setEngineStartPromise(null);
				rejectAndLogError("Failed to create floodgate engine");
				return;
			}

			// start subsequent invocations in sync mode
			const shouldStartEngineInSync = loadSynchronously || startCount > 1;
			if (shouldStartEngineInSync) {
				engine.start();
				resolve();
			} else {
				const enginePromise = engine.startAsync();
				if (!enginePromise) {
					// ideally this should never happen
					setEngineStartPromise(null);
					rejectAndLogError("Failed to get floodgate engine");
					return;
				}

				enginePromise.then(
					function onFulfilled() {
						resolve();
					}
				).catch(
					function onRejected(err: Error) {
						setEngineStartPromise(null);
						rejectAndLogError("Failed to start floodgate engine: " + (err && err.message));
					}
				);
			}
		} catch (e) {
			setEngineStartPromise(null);
			rejectAndLogError("Failed to start floodgate engine: " + (e && e.toString()));
		}
	});

	setEngineStartPromise(startPromise);

	// reset on error
	startPromise.catch((error) => {
		setEngineStartPromise(null);
	});

	return startPromise;
}

export function start(loadSynchronously: boolean = false): Promise<any> {
	return startInternal(loadSynchronously);
}

/**
 * A proxy for the engine.stop() method for consistency with the start() method.
 */
export function stop() {
	if (engine) {
		engine.stop();
	}

	setEngineStartPromise(null);
}

/**
 * Get the engine object
 * @returns the engine object
 */
export function getEngine(): FloodgateEngine {
	return engine;
}

/**
 * Display the given Survey with it's prompt
 * @param survey the survey to show
 * @returns A promise which will be rejected if the call fails.
 */
export function showSurvey(survey: IUISurvey): Promise<any> {
	return new Promise((resolve, reject) => {
		if (opened) {
			reject("Control already open");
			return;
		}

		Configuration.get().setFloodgateSurvey(survey ? survey : new DummyUISurvey());
		UI.createSurvey(function () { opened = false; });
		opened = true;
		resolve();
	});
}

/**
 * Method to allow users to launch a custom survey directly
 * @param survey the custom survey to show
 */
export function showCustomSurvey(survey: ICustomSurvey): Promise<any> {
	return showSurvey(new CustomUISurvey(survey));
}

/**
 * Reset the floodgate engine state. Used in unit tests.
 */
export function resetEngine() {
	startCount = 0;
	engineStartPromise = null;
	engine = null;
	engineTmsProvider = null;
}

/**
 * Reset the floodgate module. Used in unit tests.
 */
export function reset() {
	App_Common.reset();
	initialized = false;
	opened = false;
	resetEngine();
}

/**
 * Get a list of setting names and ids
 * @returns the setting names and ids list
 */
export function getSettingIdMap(): IFloodgateSettingIdMap {
	return IFloodgateSettingIdMap.RoamingSettingIdMap;
}

Window.setFloodgateShowCustomSurvey(showCustomSurvey);
Window.setFloodgateShowSurvey(showSurvey);
Window.setFloodgateGetEngine(getEngine);
Window.setFloodgateInitialize(initialize);
Window.setFloodgateStart(start);
Window.setFloodgateStop(stop);
Window.getSettingIdMap(getSettingIdMap);
